import { useState, useCallback, Fragment, ReactNode, useEffect } from "react";
import { ConfirmContext } from "./ConfirmContext";
import { ConfirmationDialog } from "./ConfirmationDialog";
import { DialogProps } from "@mui/material/Dialog";
import { DialogTitleProps } from "@mui/material/DialogTitle";
import { DialogContentProps } from "@mui/material/DialogContent";
import { ButtonProps } from "@mui/material/Button";
import { useTheme } from "@mui/material";

export interface ConfirmOptions {
  title?: ReactNode;
  titleProps?: DialogTitleProps;
  description?: ReactNode;
  content?: ReactNode | null;
  contentProps?: DialogContentProps;
  confirmationText?: ReactNode;
  cancellationText?: ReactNode;
  dialogProps?: Omit<DialogProps, "open">;
  confirmationButtonProps?: ButtonProps;
  cancellationButtonProps?: ButtonProps;
  actionBuilder?: (onConfirm: () => void, onCancel: () => void, loading: boolean, options: ConfirmOptions) => ReactNode;
  allowClose?: boolean;
  isAsynchronous?: boolean;
}

const DEFAULT_OPTIONS = {
  title: "Are you sure?",
  description: "",
  content: null,
  confirmationText: "Ok",
  cancellationText: "Cancel",
  dialogProps: {},
  confirmationButtonProps: {},
  cancellationButtonProps: {},
  titleProps: {},
  contentProps: {},
  allowClose: true,
};

const buildOptions = (defaultOptions: ConfirmOptions, options: ConfirmOptions) => {
  const dialogProps = {
    ...(defaultOptions.dialogProps || DEFAULT_OPTIONS.dialogProps),
    ...(options.dialogProps || {}),
  };
  const confirmationButtonProps = {
    ...(defaultOptions.confirmationButtonProps || DEFAULT_OPTIONS.confirmationButtonProps),
    ...(options.confirmationButtonProps || {}),
  };
  const cancellationButtonProps = {
    ...(defaultOptions.cancellationButtonProps || DEFAULT_OPTIONS.cancellationButtonProps),
    ...(options.cancellationButtonProps || {}),
  };
  const titleProps = {
    ...(defaultOptions.titleProps || DEFAULT_OPTIONS.titleProps),
    ...(options.titleProps || {}),
  };
  const contentProps = {
    ...(defaultOptions.contentProps || DEFAULT_OPTIONS.contentProps),
    ...(options.contentProps || {}),
  };

  return {
    ...DEFAULT_OPTIONS,
    ...defaultOptions,
    ...options,
    dialogProps,
    confirmationButtonProps,
    cancellationButtonProps,
    titleProps,
    contentProps,
  };
};

export interface IConfirmProviderProps {
  children: ReactNode | ReactNode[];
  defaultOptions?: ConfirmOptions;
}

interface IPromiseParams {
  resolve: (completed: () => void) => void;
  reject: (reason?: any) => void;
}

export const useConfirmState = () => {
  const theme = useTheme();
  const [resolveReject, setResolveReject] = useState<IPromiseParams | undefined>();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (resolveReject === undefined) {
      setTimeout(() => {
        setLoading(false);
      }, theme.transitions.duration.leavingScreen);
    }
  }, [resolveReject]);

  const handleClose = useCallback(() => {
    setResolveReject(undefined);
  }, []);

  const handleCancel = useCallback(() => {
    const reject = resolveReject?.reject;
    if (reject) {
      reject();
      handleClose();
    }
  }, [resolveReject, handleClose]);

  const handleConfirm = useCallback(() => {
    const resolve = resolveReject?.resolve;
    if (resolve) {
      setLoading(true);
      resolve(() => {
        handleClose();
      });
    }
  }, [resolveReject, handleClose]);

  return {
    loading,
    onConfirm: handleConfirm,
    onClose: handleClose,
    onCancel: handleCancel,
    setResolveReject,
    open: resolveReject !== undefined,
  };
};

export const ConfirmProvider = ({ children, defaultOptions = {} }: IConfirmProviderProps) => {
  const [options, setOptions] = useState({ ...DEFAULT_OPTIONS, ...defaultOptions });
  const { open, loading, onClose, onCancel, onConfirm, setResolveReject } = useConfirmState();

  const confirm = useCallback(
    (options: ConfirmOptions = {}) =>
      new Promise<() => void>((resolve, reject) => {
        setOptions(buildOptions(defaultOptions, options));
        setResolveReject({ resolve, reject });
      }),
    [defaultOptions, setResolveReject, setOptions]
  );

  return (
    <Fragment>
      <ConfirmContext.Provider value={confirm}>{children}</ConfirmContext.Provider>
      <ConfirmationDialog
        open={open}
        loading={loading}
        options={options}
        onClose={onClose}
        onCancel={onCancel}
        onConfirm={onConfirm}
      />
    </Fragment>
  );
};
